 import { useState, useRef, useEffect } from "react";
import { shippingzoneService } from "../services/shippingzoneService";
import { ShippingZone } from "../interfaces/ShippingZone";

type State = {
  id: number;
  name: string;
};

type Zone = {
  id: string;
  name: string;
  stateIds: number[];
  deliveryDays: string;
  freeAbove: number;
  baseCharge: number;
  active: boolean;
};

const ALL_STATES: State[] = [
  { id: 1, name: "Andhra Pradesh" },
  { id: 2, name: "Arunachal Pradesh" },
  { id: 3, name: "Assam" },
  { id: 4, name: "Bihar" },
  { id: 5, name: "Chhattisgarh" },
  { id: 6, name: "Delhi" },
  { id: 7, name: "Goa" },
  { id: 8, name: "Gujarat" },
  { id: 9, name: "Haryana" },
  { id: 10, name: "Himachal Pradesh" },
  { id: 11, name: "Jharkhand" },
  { id: 12, name: "Karnataka" },
  { id: 13, name: "Kerala" },
  { id: 14, name: "Madhya Pradesh" },
  { id: 15, name: "Maharashtra" },
  { id: 16, name: "Manipur" },
  { id: 17, name: "Meghalaya" },
  { id: 18, name: "Mizoram" },
  { id: 19, name: "Nagaland" },
  { id: 20, name: "Odisha" },
  { id: 21, name: "Punjab" },
  { id: 22, name: "Rajasthan" },
  { id: 23, name: "Sikkim" },
  { id: 24, name: "Tamil Nadu" },
  { id: 25, name: "Telangana" },
  { id: 26, name: "Tripura" },
  { id: 27, name: "Uttar Pradesh" },
  { id: 28, name: "Uttarakhand" },
  { id: 29, name: "West Bengal" },
  { id: 30, name: "Andaman & Nicobar Islands" },
  { id: 31, name: "Chandigarh" },
  { id: 32, name: "Dadra & Nagar Haveli" },
  { id: 33, name: "Daman & Diu" },
  { id: 34, name: "Jammu & Kashmir" },
  { id: 35, name: "Ladakh" },
  { id: 36, name: "Lakshadweep" },
  { id: 37, name: "Puducherry" },
];

function getStateName(id: number): string {
  return ALL_STATES.find((s) => s.id === id)?.name ?? String(id);
}

const initialZones: Zone[] = [];

type ModalMode = "none" | "add" | "edit";

const emptyZone = (): Omit<Zone, "id"> => ({
  name: "",
  stateIds: [],
  deliveryDays: "",
  freeAbove: 999,
  baseCharge: 49,
  active: true,
});

// ---- Multiselect Dropdown Component ----
// value: number[] of state IDs
// onChange: called with updated number[] of state IDs
// disabledIds: state IDs already used in other zones — shown greyed out
function MultiStateSelect({
  value,
  onChange,
  disabledIds = [],
}: {
  value: number[];
  onChange: (v: number[]) => void;
  disabledIds?: number[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = ALL_STATES.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  function toggle(id: number) {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  function selectAllFiltered() {
    // Only operate on non-disabled states
    const selectableIds = filtered.map((s) => s.id).filter((id) => !disabledIds.includes(id));
    const allSelected = selectableIds.every((id) => value.includes(id));
    if (allSelected) {
      onChange(value.filter((id) => !selectableIds.includes(id)));
    } else {
      onChange(Array.from(new Set([...value, ...selectableIds])));
    }
  }

  function clear() { onChange([]); }

  const selectableFilteredIds = filtered.map((s) => s.id).filter((id) => !disabledIds.includes(id));
  const allFilteredSelected =
    selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => value.includes(id));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div className="ms-trigger" onClick={() => setOpen((o) => !o)} style={{ cursor: "pointer" }}>
        {value.length === 0 ? (
          <span className="ms-placeholder">Select states / regions…</span>
        ) : (
          <div className="ms-chips-wrap">
            {value.slice(0, 4).map((id) => (
              <span key={id} className="ms-chip">
                {getStateName(id)}
                <button
                  type="button"
                  className="ms-chip-x"
                  onClick={(e) => { e.stopPropagation(); toggle(id); }}
                >×</button>
              </span>
            ))}
            {value.length > 4 && (
              <span className="ms-chip ms-chip-more">+{value.length - 4} more</span>
            )}
          </div>
        )}
        <span className="ms-arrow">{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="ms-dropdown">
          <div className="ms-search-wrap">
            <input
              className="ms-search"
              placeholder="Search states…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="ms-actions-row">
            <button type="button" className="ms-act-btn" onClick={selectAllFiltered}>
              {allFilteredSelected ? "Deselect Filtered" : "Select All Filtered"}
            </button>
            {value.length > 0 && (
              <button type="button" className="ms-act-btn ms-act-clear" onClick={clear}>
                Clear All
              </button>
            )}
            <span className="ms-count">{value.length} selected</span>
          </div>
          <div className="ms-list">
            {filtered.length === 0 && <div className="ms-empty">No states found</div>}
            {filtered.map((state) => {
              const isDisabled = disabledIds.includes(state.id);
              return (
                <label
                  key={state.id}
                  className={`ms-option ${value.includes(state.id) ? "checked" : ""} ${isDisabled ? "disabled" : ""}`}
                  title={isDisabled ? "Already used in another zone" : undefined}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(state.id)}
                    disabled={isDisabled}
                    onChange={() => !isDisabled && toggle(state.id)}
                  />
                  <span className="ms-option-label">{state.name}</span>
                  {isDisabled
                    ? <span className="ms-option-used">In use</span>
                    : <span className="ms-option-code">#{state.id}</span>}
                  {value.includes(state.id) && <span className="ms-check">✓</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function ShippingPage() {
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);

  function showToast(message: string, type: "success" | "error" | "info" = "success") {
    setToast({ message, type });
  }

  const [zones, setZones] = useState<Zone[]>(initialZones);
  const [modalMode, setModalMode] = useState<ModalMode>("none");
  const [selected, setSelected] = useState<Zone | null>(null);
  const [form, setForm] = useState<Omit<Zone, "id">>(emptyZone());
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  function openAdd() { setForm(emptyZone()); setModalMode("add"); }
  function openEdit(z: Zone) { setForm({ ...z }); setSelected(z); setModalMode("edit"); }
  function closeModal() { setModalMode("none"); setSelected(null); }

  async function toggleZone(id: string) {
    const num = Number(id);
    const response = await shippingzoneService.InactiveZone(num);
    if (response.isSuccess) {
      showToast(response.message, "success");
      Loadshippingzone();
    }
    setZones((p) => p.map((z) => (z.id === id ? { ...z, active: !z.active } : z)));
  }

  async function deleteZone(id: string) {
    const num = Number(id);
    const response = await shippingzoneService.Deletezone(num);
    if (response.isSuccess) {
      showToast(response.message, "success");
      Loadshippingzone();
    }
    setZones((p) => p.filter((z) => z.id !== id));
  }

  function f<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  useEffect(() => {
    Loadshippingzone();
  }, []);

  async function save() {
    if (!form.name || form.stateIds.length === 0) return;
    try {
      if (modalMode === "add") {
        const response = await shippingzoneService.SaveZone(form);
        if (response) {
          setZones((prev) => [...prev, { ...form, id: response.data.id || `z-${Date.now()}` }]);
          showToast("Shipping Zone added successfully", "success");
        }
      } else if (selected) {
        const response = await shippingzoneService.SaveZone({ ...form, id: selected.id });
        if (response) {
          setZones((prev) => prev.map((z) => z.id === selected.id ? { ...form, id: selected.id } : z));
          showToast("Shipping Zone updated successfully", "success");
        }
      }
      closeModal();
    } catch (error) {
      console.error("Failed to save shipping zone", error);
      showToast("Something went wrong while saving", "error");
    }
  }

  async function Loadshippingzone() {
    try {
      const data = await shippingzoneService.GetShippingzone();
      const mapped: Zone[] = data.map((b: any) => ({
        id: b.id?.toString(),
        name: b.zone || "",
        stateIds: Array.isArray(b.states)
          ? b.states.map((x: any) => Number(x))
          : typeof b.states === "string"
            ? b.states.split(",").map((x: string) => Number(x.trim())).filter((n: number) => !isNaN(n))
            : [],
        deliveryDays: b.deliverydays || "",
        freeAbove: b.freeshippingabove || 0,
        baseCharge: b.basecharge || 0,
        active: b.isActive ?? false,
      }));
      setZones(mapped);
    } catch (error) {
      console.error("Failed to load shipping zones", error);
    }
  }

  // Compute which state IDs are already used in other zones (excluding the one being edited)
  const disabledStateIds = zones
    .filter((z) => z.id !== selected?.id)
    .flatMap((z) => z.stateIds);

  return (
    <>
      <style>{`
        .ms-trigger { display:flex; align-items:center; justify-content:space-between; gap:8px; padding:8px 12px; border:1.5px solid #ede6df; border-radius:10px; background:#fdf8f3; min-height:42px; transition:border .2s,box-shadow .2s; }
        .ms-trigger:hover { border-color:#b09a88; }
        .ms-trigger:focus-within { border-color:var(--theme); box-shadow:0 0 0 3px var(--theme-glow); background:#fff; }
        .ms-placeholder { color:#b09a88; font-size:13.5px; }
        .ms-chips-wrap { display:flex; flex-wrap:wrap; gap:5px; flex:1; }
        .ms-chip { display:inline-flex; align-items:center; gap:4px; background:var(--theme-light); color:var(--theme); padding:2px 8px 2px 10px; border-radius:20px; font-size:12px; font-weight:600; }
        .ms-chip-x { background:none; border:none; cursor:pointer; color:var(--theme); font-size:14px; line-height:1; padding:0 2px; opacity:.7; }
        .ms-chip-x:hover { opacity:1; }
        .ms-chip-more { background:#f0ebe4; color:#7a6652; }
        .ms-arrow { color:#b09a88; font-size:10px; flex-shrink:0; }
        .ms-dropdown { position:absolute; top:calc(100% + 6px); left:0; right:0; background:#fff; border:1.5px solid #ede6df; border-radius:12px; box-shadow:0 8px 32px rgba(26,18,8,.14); z-index:500; overflow:hidden; }
        .ms-search-wrap { padding:10px 12px 6px; border-bottom:1px solid #f5f0eb; }
        .ms-search { width:100%; padding:8px 12px; border:1.5px solid #ede6df; border-radius:8px; font-size:13px; outline:none; font-family:inherit; background:#fdf8f3; }
        .ms-search:focus { border-color:var(--theme); }
        .ms-actions-row { display:flex; align-items:center; gap:8px; padding:7px 12px; border-bottom:1px solid #f5f0eb; background:#fdf8f3; }
        .ms-act-btn { background:none; border:none; cursor:pointer; font-size:12px; font-weight:600; color:var(--theme); font-family:inherit; padding:2px 6px; border-radius:6px; }
        .ms-act-btn:hover { background:var(--theme-light); }
        .ms-act-clear { color:#ef4444; }
        .ms-act-clear:hover { background:#fee2e2; }
        .ms-count { margin-left:auto; font-size:11px; color:#b09a88; font-weight:600; }
        .ms-list { max-height:220px; overflow-y:auto; }
        .ms-option { display:flex; align-items:center; gap:9px; padding:9px 14px; cursor:pointer; font-size:13px; color:#4a3728; transition:background .12s; user-select:none; }
        .ms-option:hover, .ms-option.checked { background:#fdf8f3; }
        .ms-option input { width:16px; height:16px; accent-color:var(--theme); cursor:pointer; flex-shrink:0; }
        .ms-option-label { flex:1; }
        .ms-option-code { font-size:10.5px; color:#b09a88; font-weight:700; letter-spacing:.5px; background:#f5f0eb; padding:1px 6px; border-radius:6px; }
        .ms-option.disabled { opacity:.5; cursor:not-allowed; background:#fafafa; }
        .ms-option.disabled input { cursor:not-allowed; }
        .ms-option-used { font-size:10px; color:#ef4444; font-weight:700; background:#fee2e2; padding:1px 7px; border-radius:6px; white-space:nowrap; }
        .ms-check { color:var(--theme); font-size:13px; font-weight:700; }
        .ms-empty { padding:18px; text-align:center; color:#b09a88; font-size:13px; }
        .sh-topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:20px; flex-wrap:wrap; gap:12px; }
        .sh-btn-primary { background:linear-gradient(135deg,#d4551a,var(--theme)); color:#fff; border:none; border-radius:10px; padding:9px 20px; font-size:14px; font-weight:700; cursor:pointer; display:flex; align-items:center; gap:7px; box-shadow:0 2px 8px var(--theme-glow); font-family:inherit; }
        .sh-btn-primary:hover { filter:brightness(1.08); transform:translateY(-1px); }
        .sh-card { background:#fff; border-radius:14px; border:1px solid #f1f5f9; box-shadow:0 2px 8px rgba(0,0,0,.05); padding:20px 22px; margin-bottom:14px; display:grid; grid-template-columns:1fr auto; gap:16px; align-items:start; }
        .sh-card.inactive { opacity:.55; }
        .sh-zone-name { font-size:16px; font-weight:800; color:#1e293b; margin-bottom:4px; }
        .sh-zone-days { font-size:13px; color:#64748b; margin-bottom:10px; }
        .sh-pricing { display:flex; gap:12px; flex-wrap:wrap; margin-bottom:10px; }
        .sh-price-chip { padding:5px 12px; border-radius:10px; font-size:13px; font-weight:600; }
        .sh-price-chip-free { background:#dcfce7; color:#15803d; }
        .sh-price-chip-base { background:#dbeafe; color:#1d4ed8; }
        .sh-states { display:flex; flex-wrap:wrap; gap:5px; }
        .sh-state { background:#f1f5f9; color:#475569; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
        .sh-right { display:flex; flex-direction:column; align-items:flex-end; gap:8px; }
        .sh-toggle { position:relative; width:44px; height:24px; }
        .sh-toggle input { opacity:0; width:0; height:0; }
        .sh-slider { position:absolute; cursor:pointer; inset:0; background:#e2e8f0; border-radius:24px; transition:.3s; }
        .sh-slider:before { content:""; position:absolute; width:18px; height:18px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:.3s; }
        input:checked + .sh-slider { background:var(--theme); }
        input:checked + .sh-slider:before { transform:translateX(20px); }
        .sh-actions { display:flex; gap:6px; }
        .sh-btn-sm { padding:5px 12px; border-radius:8px; border:1.5px solid #e2e8f0; background:#fff; font-size:12px; font-weight:600; cursor:pointer; color:#475569; font-family:inherit; }
        .sh-btn-del { border-color:#fee2e2; color:#ef4444; }
        .sh-btn-del:hover { background:#fee2e2; }
        .ns-overlay { position:fixed; inset:0; background:rgba(15,23,42,.55); backdrop-filter:blur(4px); z-index:1000; display:flex; align-items:flex-start; justify-content:center; padding:24px 16px; overflow-y:auto; }
        .ns-modal { background:#fff; border-radius:20px; width:100%; max-width:580px; box-shadow:0 24px 60px rgba(0,0,0,.2); margin:auto; animation:mIn .25s ease; }
        @keyframes mIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        .ns-modal-head { display:flex; justify-content:space-between; align-items:center; padding:20px 24px; border-bottom:1px solid #f1f5f9; }
        .ns-modal-title { font-size:17px; font-weight:700; color:#1e293b; }
        .ns-modal-close { width:34px; height:34px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; font-size:16px; color:#64748b; display:flex; align-items:center; justify-content:center; }
        .ns-modal-body { padding:22px 24px; max-height:65vh; overflow-y:auto; display:flex; flex-direction:column; gap:14px; }
        .ns-modal-foot { padding:16px 24px; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:10px; }
        .ns-field2 { display:flex; flex-direction:column; gap:5px; }
        .ns-label2 { font-size:11.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.5px; }
        .ns-input2 { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:14px; outline:none; box-sizing:border-box; font-family:inherit; }
        .ns-input2:focus { border-color:var(--theme); box-shadow:0 0 0 3px var(--theme-glow); }
        .ns-row2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
        .ns-btn-sec { background:#f8fafc; color:#475569; border:1.5px solid #e2e8f0; border-radius:10px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; }
        .ns-btn-blue { background:linear-gradient(135deg,#d4551a,var(--theme)); color:#fff; border:none; border-radius:10px; padding:9px 20px; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; }
        .ns-selected-count { font-size:12px; color:var(--theme); font-weight:600; }
        .ns-del-overlay { position:fixed; inset:0; z-index:2000; background:rgba(26,18,8,.48); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; padding:20px; animation:ns-del-fade .15s ease; }
        @keyframes ns-del-fade { from{opacity:0} to{opacity:1} }
        .ns-del-modal { background:#fff; border-radius:20px; box-shadow:0 24px 64px rgba(26,18,8,.2); width:100%; max-width:400px; overflow:hidden; animation:ns-del-pop .2s cubic-bezier(.34,1.56,.64,1); }
        @keyframes ns-del-pop { from{opacity:0;transform:scale(.9)} to{opacity:1;transform:scale(1)} }
        .ns-del-top { display:flex; flex-direction:column; align-items:center; text-align:center; padding:36px 28px 22px; gap:14px; }
        .ns-del-icon { width:64px; height:64px; border-radius:50%; background:#fef2f2; border:2px solid #fecaca; display:flex; align-items:center; justify-content:center; }
        .ns-del-icon svg { color:#ef4444; }
        .ns-del-title { font-size:18px; font-weight:800; color:#1a1208; }
        .ns-del-desc { font-size:13.5px; color:#78716c; line-height:1.6; max-width:280px; }
        .ns-del-hr { height:1px; background:#f5f0eb; }
        .ns-del-btns { display:flex; gap:10px; padding:18px 20px; }
        .ns-del-cancel { flex:1; padding:11px 0; border-radius:11px; border:1.5px solid #e8e0d8; background:#fdf8f3; font-size:14px; font-weight:600; color:#78716c; cursor:pointer; font-family:inherit; }
        .ns-del-confirm { flex:1; padding:11px 0; border-radius:11px; border:none; background:linear-gradient(135deg,#ef4444,#dc2626); color:#fff; font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; }
      `}</style>

      <div>
        <div className="sh-topbar">
          <div style={{ color: "#64748b", fontSize: 14 }}>
            {zones.length} shipping zones · {zones.filter((z) => z.active).length} active
          </div>
          <button className="sh-btn-primary" onClick={openAdd} type="button">+ Add Zone</button>
        </div>

        {zones.map((z) => (
          <div key={z.id} className={`sh-card ${!z.active ? "inactive" : ""}`}>
            <div>
              <div className="sh-zone-name">🗺️ {z.name}</div>
              <div className="sh-zone-days">⏱️ Delivery: {z.deliveryDays} business days</div>
              <div className="sh-pricing">
                <span className="sh-price-chip sh-price-chip-free">Free above ₹{z.freeAbove.toLocaleString()}</span>
                <span className="sh-price-chip sh-price-chip-base">Base charge ₹{z.baseCharge}</span>
              </div>
              <div className="sh-states">
                {z.stateIds.map((id) => (
                  <span key={id} className="sh-state">{getStateName(id)}</span>
                ))}
              </div>
            </div>
            <div className="sh-right">
              <label className="sh-toggle">
                <input type="checkbox" checked={z.active} onChange={() => toggleZone(z.id)} />
                <span className="sh-slider"></span>
              </label>
              <div className="sh-actions">
                <button className="sh-btn-sm" onClick={() => openEdit(z)} type="button">Edit</button>
                <button className="sh-btn-sm sh-btn-del" onClick={() => setPendingDeleteId(z.id)} type="button">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(modalMode === "add" || modalMode === "edit") && (
        <div className="ns-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="ns-modal">
            <div className="ns-modal-head">
              <span className="ns-modal-title">{modalMode === "add" ? "Add Shipping Zone" : "Edit Zone"}</span>
              <button className="ns-modal-close" onClick={closeModal} type="button">✕</button>
            </div>
            <div className="ns-modal-body">
              <div className="ns-field2">
                <label className="ns-label2">Zone Name *</label>
                <input className="ns-input2" value={form.name} onChange={(e) => f("name", e.target.value)} placeholder="e.g. Metro Cities" />
              </div>

              <div className="ns-field2">
                <label className="ns-label2" style={{ display: "flex", justifyContent: "space-between" }}>
                  States / Regions *
                  {form.stateIds.length > 0 && (
                    <span className="ns-selected-count">{form.stateIds.length} selected</span>
                  )}
                </label>
                {/*
                  value    = number[] of IDs stored in form
                  onChange = updates form.stateIds
                  disabledIds = all stateIds used in other zones (excludes current zone in edit mode)
                */}
                <MultiStateSelect
                  value={form.stateIds}
                  onChange={(v) => f("stateIds", v)}
                  disabledIds={disabledStateIds}
                />
              </div>

              <div className="ns-row2">
                <div className="ns-field2">
                  <label className="ns-label2">Delivery Days</label>
                  <input className="ns-input2" value={form.deliveryDays} onChange={(e) => f("deliveryDays", e.target.value)} placeholder="e.g. 2-3" />
                </div>
                <div className="ns-field2">
                  <label className="ns-label2">Base Charge (₹)</label>
                  <input className="ns-input2" type="number" value={form.baseCharge} onChange={(e) => f("baseCharge", Number(e.target.value))} />
                </div>
              </div>

              <div className="ns-field2">
                <label className="ns-label2">Free Shipping Above (₹)</label>
                <input className="ns-input2" type="number" value={form.freeAbove} onChange={(e) => f("freeAbove", Number(e.target.value))} />
              </div>

              <div className="ns-field2">
                <label className="ns-label2">Status</label>
                <select className="ns-input2" value={form.active ? "active" : "inactive"} onChange={(e) => f("active", e.target.value === "active")}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
            <div className="ns-modal-foot">
              <button className="ns-btn-sec" onClick={closeModal} type="button">Cancel</button>
              <button className="ns-btn-blue" onClick={save} type="button">{modalMode === "add" ? "Add Zone" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteId && (
        <div className="ns-del-overlay" onClick={(e) => { if (e.target === e.currentTarget) setPendingDeleteId(null); }}>
          <div className="ns-del-modal" role="dialog">
            <div className="ns-del-top">
              <div className="ns-del-icon">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" /><path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </div>
              <div className="ns-del-title">Delete Shipping Zone?</div>
              <p className="ns-del-desc">This action cannot be undone. This shipping zone will be permanently removed.</p>
            </div>
            <div className="ns-del-hr" />
            <div className="ns-del-btns">
              <button type="button" className="ns-del-cancel" onClick={() => setPendingDeleteId(null)}>Cancel</button>
              <button type="button" className="ns-del-confirm" onClick={() => { deleteZone(pendingDeleteId); setPendingDeleteId(null); }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}